/**
 * Decoração de pessoa (profissional ou cliente) para as listas de picker.
 *
 * Existe para ser calculada UMA vez por pessoa, num getter Vuex compartilhado,
 * em vez de uma vez por pessoa por instância de picker. O motivo é memória: a
 * Lista de Tarefas monta 2 `TaskAssigneePicker` + 1 `TaskClientPicker` por linha,
 * e cada um decorava a lista inteira do escritório (197 profissionais + 689
 * clientes no escritório 83).
 *
 * A divisão importante é entre o que é INVARIANTE por pessoa e o que depende da
 * linha:
 *
 *  - invariante (aqui): nome, iniciais, avatar, cores derivadas do nome
 *  - por linha (fica no componente): `selected` (depende do `value` da tarefa) e
 *    `marked` (depende de `markResponsavel`)
 *
 * Os componentes leem os flags por linha de mapas indexados pela `key`/`id`, sem
 * recriar os objetos da lista — é isso que mantém a lista compartilhada.
 */
import { stringToColor, textOnColor } from '@modules/global/utils/Color'

export function personName(person) {
  if (!person) return ''
  return person.nome || person.name || ''
}

export function personAvatar(person) {
  if (!person) return null
  return person.avatarUrl || person.avatar_url || null
}

// Cor determinística por pessoa para que responsáveis diferentes se distingam
// nas pilhas de avatar. Usa o mesmo hash-to-HSL do professional-avatar.
export function personBgColor(person) {
  const name = personName(person)
  const fallback =
    person && person.id != null ? String(person.id) + ':' + person.tipo : ''
  return stringToColor(name || fallback || '?', {
    saturation: 55,
    lightness: 45,
  })
}

export function personTextColor(person) {
  return textOnColor(personBgColor(person))
}

export function personInitials(nome) {
  if (!nome) return '?'
  const parts = nome.trim().split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return parts[0][0].toUpperCase()
}

/**
 * View-model invariante de uma pessoa. `key` é o identificador composto que os
 * mapas de seleção usam — mesma forma que `selectedKeys` sempre usou
 * (`id + '_' + tipo`), para o lookup no template não precisar de concatenação.
 */
export function decoratePerson(person, tipo) {
  const avatar = personAvatar(person)
  const nome = personName(person)
  return {
    raw: person,
    id: person.id,
    tipo: tipo,
    key: person.id + '_' + tipo,
    nome: nome,
    initials: personInitials(nome),
    avatar: avatar,
    bgColor: avatar ? null : personBgColor(person),
    textColor: avatar ? null : personTextColor(person),
  }
}

export function decoratePeople(people, tipo) {
  if (!Array.isArray(people)) return []
  return people.map(function (person) {
    return decoratePerson(person, tipo)
  })
}
